import { array, bool, string } from 'yup';
import s3jsonConstants from '../../constants/s3jsonConstants';
import { nullToEmptyString, nullToArray } from './transforms';

const isPlatinumParty = (maskEnhancements: string[] | undefined) =>
  maskEnhancements?.includes(s3jsonConstants['Platinum Party ($)'])
    ? true
    : maskEnhancements?.includes(s3jsonConstants['Platinum Party – $65']);

const generateSVZoneValidation = (zone: number) =>
  string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(s3jsonConstants['Dermaplaning ($$)']),
      then: string().required(
        `Exfoliation Enhancements Zone ${zone} Stroke Volume`,
      ),
    })
    .nullable(true)
    .transform(nullToEmptyString);

export const facialTreatmentRecordCommonSchema = {
  exfoliationSegment: bool().nullable(true),
  sosSegment: bool().nullable(true),
  exfoliationEnhancement: array(string()).nullable(true),
  exfoliationDtmWand: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(
          s3jsonConstants['Diamond Tip Microderm — $80'],
        ),
      then: string().required('Exfoliation Enhancements Wand'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  exfoliationDtmSuctionRate: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(
          s3jsonConstants['Diamond Tip Microderm — $80'],
        ),
      then: string().required('Exfoliation Enhancements Suction Rate'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  exfoliationDtmPasses: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(
          s3jsonConstants['Diamond Tip Microderm — $80'],
        ),
      then: string().required('Exfoliation Enhancements Passes'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  exfoliationHwmWandProduct: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(
          s3jsonConstants['Hydro Wand Microderm — $80'],
        ),
      then: string()
        .required('Exfoliation Enhancements Wand Product')
        .min(1, 'Exfoliation Enhancements Wand Product'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  exfoliationHwmSuctionRate: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(
          s3jsonConstants['Hydro Wand Microderm — $80'],
        ),
      then: string().required('Exfoliation Enhancements Suction Rate'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  exfoliationHwmPasses: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(
          s3jsonConstants['Hydro Wand Microderm — $80'],
        ),
      then: string().required('Exfoliation Enhancements Passes'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  extractionsZone: array(string())
    .when('extractionsSegment', {
      is: true,
      then: array(string())
        .required('Extractions Zone')
        .min(1, 'Extractions Zone'),
    })
    .nullable(true)
    .transform(nullToArray),
  exfoliationStrokeVolumeZoneOne: generateSVZoneValidation(1),
  exfoliationStrokeVolumeZoneTwo: generateSVZoneValidation(2),
  exfoliationStrokeVolumeZoneThree: generateSVZoneValidation(3),
  exfoliationStrokeVolumeZoneFour: generateSVZoneValidation(4),
  maskEnhancements: array(string()).nullable(true),
  maskPpMicrocurrentProgram: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        isPlatinumParty(maskEnhancements),
      then: string().optional(),
    })
    .nullable()
    .transform(nullToEmptyString),
  maskPpMicrocurrentIntensity: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        isPlatinumParty(maskEnhancements),
      then: string().optional(),
    })
    .nullable()
    .transform(nullToEmptyString),
  maskPpAdditionalTouchpoint: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        isPlatinumParty(maskEnhancements),
      then: array(string())
        .optional()
        .min(1, 'Platinum Party Additional Touchpoint'),
    })
    .nullable()
    .transform(nullToEmptyString),
  maskLedPanelTime: string()
    .when('maskEnhancements', {
      is: (maskEnhancements?: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['LED Panel — $50']),
      then: string().required('Mask Segment LED Panel Time'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  maskLedTouchpoint: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['LED Panel — $50']),
      then: array(string())
        .required('Mask Segment LED Touchpoint')
        .min(1, 'Mask Segment LED Touchpoint'),
    })
    .nullable(true)
    .transform(nullToArray),
  maskMicrocurrentProgramLpi: string()
    .when('maskEnhancements', {
      is: (maskEnhancements?: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Microcurrent — $80']),
      then: string().optional(),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  maskMicrocurrentProgramSpi: string()
    .when('maskEnhancements', {
      is: (maskEnhancements?: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Microcurrent — $80']),
      then: string().required('Mask Segment Short Program Intensity'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  maskNanoinfusionStitchDepthFcc: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
      then: string().required(
        'Nano Mask Stitch depth for forehead, cheeks, and chin',
      ),
    })
    .nullable()
    .transform(nullToEmptyString),
  maskNanoinfusionSpeedFcc: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
      then: string().required('Nano Mask Speed for forehead, cheeks, and chin'),
    })
    .nullable()
    .transform(nullToEmptyString),
  maskNanoinfusionStitchDepthNe: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
      then: string().required('Nano Mask Stitch depth for nose and eyes'),
    })
    .nullable()
    .transform(nullToEmptyString),
  maskNanoinfusionSpeedNe: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
      then: string().required('Nano Mask Speed for nose and eyes'),
    })
    .nullable()
    .transform(nullToEmptyString),
  exfoliationPeelProduct: array(string())
    .when(['exfoliationEnhancement', 'exfoliationPeelProductTp'], {
      is: (
        exfoliationEnhancement: string[],
        exfoliationPeelProductTp: string,
      ) =>
        !exfoliationPeelProductTp &&
        exfoliationEnhancement?.includes(s3jsonConstants['Peel — $50']),
      then: array(string())
        .required('Exfoliation Enhancements Peel Product')
        .min(1, 'Exfoliation Enhancements Peel Product'),
    })
    .nullable(true)
    .transform(nullToArray),
  exfoliationPeelTimeOnSkin: string()
    .when('exfoliationEnhancement', {
      is: (exfoliationEnhancement: string[]) =>
        exfoliationEnhancement?.includes(s3jsonConstants['Peel — $50']),
      then: string().required('Exfoliation Enhancements Time on Skin'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  extractionsSegment: bool().nullable(true),
  maskSegment: bool().nullable(true),
  nextTimeEnhancement: array()
    .required('Next Time Enhancements')
    .min(1, 'Next Time Enhancements')
    .nullable(true)
    .transform(nullToArray),
  holidayHydrationMask: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Holiday Hydration']),
      then: array(string())
        .required('Holiday Hydration Mask')
        .min(1, 'Holiday Hydration Mask'),
    })
    .nullable(true)
    .transform(nullToArray),
  holidayHydrationLedTt: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Holiday Hydration']),
      then: string().required('LED Treatment Time'),
    })
    .nullable(true)
    .transform(nullToArray),
  holidayHydrationLedAtp: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Holiday Hydration']),
      then: string().required('LED Additional Touch Point'),
    })
    .nullable(true)
    .transform(nullToArray),
  holidayHydrationLedAtpa: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Holiday Hydration']),
      then: array(string())
        .required('LED Additional Touch Point Aromatherapy')
        .min(1, 'LED Additional Touch Point Aromatherapy'),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningBoostNanoSerum: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string())
        .required('Brightening Boost Nano Serum')
        .min(1, 'Brightening Boost Nano Serum'),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningBoostNanoMask: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string())
        .required('Brightening Boost Nano Mask')
        .min(1, 'Brightening Boost Nano Mask'),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningBoostStitchDepth: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string()).optional(),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningBoostSpeed: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string()).optional(),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningLedTreatmentTime: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string())
        .required('Brightening Boost LED treatment time')
        .min(1, 'Brightening Boost LED treatment time'),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningLedAdditionalTouchPoint: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string())
        .required('Brightening Boost LED additional touch point')
        .min(1, 'Brightening Boost LED additional touch point'),
    })
    .nullable(true)
    .transform(nullToArray),
  brighteningAdditionalTouchPointProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Brightening Boost']),
      then: array(string())
        .required('Brightening Boost Additional Touch point product')
        .min(1, 'Brightening Boost Additional Touch point product'),
    })
    .nullable(true)
    .transform(nullToArray),
  exosomeBioBoostNanoSerum: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Exosome BioBoost']),
      then: string().required('Exosome BioBoost Nano Serum'),
    })
    .nullable()
    .transform(nullToEmptyString),
  exosomeBioBoostNanoMask: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Exosome BioBoost']),
      then: array(string())
        .required('Exosome BioBoost Nano Mask')
        .min(1, 'Exosome BioBoost Nano Mask'),
    })
    .nullable(true)
    .transform(nullToArray),
  exosomeBioBoostLedMask: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Exosome BioBoost']),
      then: string().required('Exosome BioBoost LED Mask'),
    })
    .nullable()
    .transform(nullToEmptyString),
  exosomeBioBoostLedTreatmentTime: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Exosome BioBoost']),
      then: string().required('Exosome BioBoost LED Treatment Time'),
    })
    .nullable()
    .transform(nullToEmptyString),
  exosomeBioBoostLedAdditionalTouchPoint: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Exosome BioBoost']),
      then: array(string())
        .required('Exosome BioBoost LED Additional Touch Point')
        .min(1, 'Exosome BioBoost LED Additional Touch Point'),
    })
    .nullable(true)
    .transform(nullToArray),
  exosomeBioBoostLedAdditionalTouchPointProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Exosome BioBoost']),
      then: array(string())
        .required('Exosome BioBoost Additional Touch Point Product')
        .min(1, 'Exosome BioBoost Additional Touch Point Product'),
    })
    .nullable(true)
    .transform(nullToArray),
  quenchCoolBoostNanoProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Quench + Cool Boost']),
      then: array(string())
        .required('Mask Segment Quench + Cool Boost Nano Product')
        .min(1, 'Mask Segment Quench + Cool Boost Nano Product'),
    })
    .nullable()
    .transform(nullToEmptyString),
  quenchCoolBoostNanoMask: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Quench + Cool Boost']),
      then: array(string())
        .required('Mask Segment Quench + Cool Boost Nano Mask')
        .min(1, 'Mask Segment Quench + Cool Boost Nano Mask'),
    })
    .nullable()
    .transform(nullToEmptyString),
  quenchCoolBoostAdditionalTouchPointProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['Quench + Cool Boost']),
      then: array(string())
        .required(
          'Mask Segment Quench + Cool Boost Additional Touch Point Product',
        )
        .min(
          1,
          'Mask Segment Quench + Cool Boost Additional Touch Point Product',
        ),
    })
    .nullable()
    .transform(nullToEmptyString),

  vennPdrnBoostProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['VENN PDRN Boost']),
      then: array(string())
        .required('VENN PDRN Boost Nano Serum')
        .min(1, 'VENN PDRN Boost Nano Serum'),
    })
    .nullable(true)
    .transform(nullToArray),
  vennPdrnBoostProductMask: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['VENN PDRN Boost']),
      then: array(string())
        .required('VENN PDRN Boost Mask')
        .min(1, 'VENN PDRN Boost Mask'),
    })
    .nullable(true)
    .transform(nullToArray),
  vennPdrnBoostProductMicrocurrentProgramLongLevel: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[] | undefined) =>
        maskEnhancements?.includes(s3jsonConstants['VENN PDRN Boost']),
      then: string().required(
        'VENN PDRN Boost Microcurrent Program Long Level',
      ),
    })
    .nullable()
    .transform(nullToEmptyString),
  vennPdrnBoostProductMicrocurrentProgramIntensity: string()
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['VENN PDRN Boost']),
      then: string().required('VENN PDRN Boost Microcurrent Program Intensity'),
    })
    .nullable(true)
    .transform(nullToEmptyString),
  vennPdrnBoostProductAdditionalTouchPointProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['VENN PDRN Boost']),
      then: array(string())
        .required('VENN PDRN Boost Additional Touch Point Product')
        .min(1, 'VENN PDRN Boost Additional Touch Point Product'),
    })
    .nullable(true)
    .transform(nullToArray),

  growthFactorOxyGenNanoSerum: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(
          s3jsonConstants['Growth Factor OxyGen Boost'],
        ),
      then: array(string())
        .required('Growth Factor OxyGen Nano Serum')
        .min(1, 'Growth Factor OxyGen Nano Serum'),
    })
    .nullable(true)
    .transform(nullToArray),
  growthFactorOxyGenMask: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(
          s3jsonConstants['Growth Factor OxyGen Boost'],
        ),
      then: array(string())
        .required('Growth Factor OxyGen Mask')
        .min(1, 'Growth Factor OxyGen Mask'),
    })
    .nullable(true)
    .transform(nullToArray),
  growthFactorOxyGenMicrocurrentProgram: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(
          s3jsonConstants['Growth Factor OxyGen Boost'],
        ),
      then: array(string())
        .required('Growth Factor OxyGen Microcurrent Program')
        .min(1, 'Growth Factor OxyGen Microcurrent Program'),
    })
    .nullable(true)
    .transform(nullToArray),
  growthFactorOxyGenAdditionalTouchPointProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(
          s3jsonConstants['Growth Factor OxyGen Boost'],
        ),
      then: array(string())
        .required('Growth Factor OxyGen Additional Touch Point Product')
        .min(1, 'Growth Factor OxyGen Additional Touch Point Product'),
    })
    .nullable(true)
    .transform(nullToArray),

  clarifyingAcneBoostMaskSerum: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Clarifying Acne Boost']),
      then: array(string())
        .required('Clarifying Acne Boost Mask Serum')
        .min(1, 'Clarifying Acne Boost Mask Serum'),
    })
    .nullable(true)
    .transform(nullToArray),
  clarifyingAcneBoostMaskBase: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Clarifying Acne Boost']),
      then: array(string())
        .required('Clarifying Acne Boost Mask Base')
        .min(1, 'Clarifying Acne Boost Mask Base'),
    })
    .nullable(true)
    .transform(nullToArray),
  clarifyingAcneBoostToner: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Clarifying Acne Boost']),
      then: array(string())
        .required('Clarifying Acne Boost Toner')
        .min(1, 'Clarifying Acne Boost Toner'),
    })
    .nullable(true)
    .transform(nullToArray),
  clarifyingAcneBoostTreatmentTime: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Clarifying Acne Boost']),
      then: array(string())
        .required('Clarifying Acne Boost Treatment Time')
        .min(1, 'Clarifying Acne Boost Treatment Time'),
    })
    .nullable(true)
    .transform(nullToArray),
  clarifyingAcneBoostAdditionalTouchPoint: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Clarifying Acne Boost']),
      then: array(string())
        .required('Clarifying Acne Boost Additional Touch Point')
        .min(1, 'Clarifying Acne Boost Additional Touch Point'),
    })
    .nullable(true)
    .transform(nullToArray),
  clarifyingAcneBoostAdditionalTouchPointProduct: array(string())
    .when('maskEnhancements', {
      is: (maskEnhancements: string[]) =>
        maskEnhancements?.includes(s3jsonConstants['Clarifying Acne Boost']),
      then: array(string())
        .required('Clarifying Acne Boost Additional Touch Point Product')
        .min(1, 'Clarifying Acne Boost Additional Touch Point Product'),
    })
    .nullable(true)
    .transform(nullToArray),
};
